import { useState, useEffect } from 'react';
import { Plus, Calendar, Clock, CheckCircle, XCircle, AlertCircle, User, Filter, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { api } from '../../core/apiClient';
import { formatDate } from '../../lib/utils';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

interface Employee {
  id: string;
  code: string;
  firstName: string;
  lastName: string;
}

interface Absence {
  id: string;
  employeeId: string;
  employee?: Employee;
  type: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  status: string;
  reason?: string;
  approvedBy?: string;
  approvedAt?: string;
  documentUrl?: string;
}

const ABSENCE_TYPES = [
  { value: 'VACANCES', label: 'Vacances', color: 'bg-blue-100 text-blue-800' },
  { value: 'BAIXA_MALALTIA', label: 'Baixa per malaltia', color: 'bg-red-100 text-red-800' },
  { value: 'PERMIS_RETRIBUIT', label: 'Permís retribuït', color: 'bg-green-100 text-green-800' },
  { value: 'PERMIS_NO_RETRIBUIT', label: 'Permís no retribuït', color: 'bg-amber-100 text-amber-800' },
  { value: 'MATERNITAT', label: 'Maternitat', color: 'bg-pink-100 text-pink-800' },
  { value: 'PATERNITAT', label: 'Paternitat', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'DEFUNCIO', label: 'Defunció familiar', color: 'bg-slate-100 text-slate-800' },
  { value: 'MATRIMONI', label: 'Matrimoni', color: 'bg-purple-100 text-purple-800' },
  { value: 'MUDANCA', label: 'Mudança', color: 'bg-orange-100 text-orange-800' },
  { value: 'ALTRES', label: 'Altres', color: 'bg-slate-100 text-slate-800' },
];

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  PENDENT: { label: 'Pendent', color: 'bg-amber-100 text-amber-800', icon: AlertCircle },
  APROVADA: { label: 'Aprovada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  REBUTJADA: { label: 'Rebutjada', color: 'bg-red-100 text-red-800', icon: XCircle },
  CANCELADA: { label: 'Cancel·lada', color: 'bg-slate-100 text-slate-800', icon: XCircle },
};

export default function Absences() {
  const navigate = useNavigate();
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState<Absence | null>(null);

  const [filters, setFilters] = useState({
    employeeId: '',
    type: '',
    status: '',
    year: new Date().getFullYear().toString(),
  });

  const [formData, setFormData] = useState({
    employeeId: '',
    type: 'VACANCES',
    startDate: '',
    endDate: '',
    reason: '',
  });

  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    totalDays: 0,
  });

  useEffect(() => {
    loadEmployees();
    loadAbsences();
  }, []);

  useEffect(() => {
    loadAbsences();
  }, [filters]);

  const loadEmployees = async () => {
    try {
      const response = await api.get<any>('/employees');
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('[v0] Error loading employees:', error);
    }
  };

  const loadAbsences = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.employeeId) params.append('employeeId', filters.employeeId);
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.year) params.append('year', filters.year);

      const response = await api.get<any>(`/absences?${params.toString()}`);
      const data = response.data?.data || [];
      setAbsences(data);

      // Calculate stats
      const pending = data.filter((a: Absence) => a.status === 'PENDENT').length;
      const approved = data.filter((a: Absence) => a.status === 'APROVADA').length;
      const totalDays = data
        .filter((a: Absence) => a.status === 'APROVADA')
        .reduce((sum: number, a: Absence) => sum + a.totalDays, 0);
      
      setStats({ pending, approved, totalDays });
    } catch (error) {
      console.error('[v0] Error loading absences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        reason: formData.reason || undefined,
      };

      if (editingAbsence) {
        await api.put(`/absences/${editingAbsence.id}`, payload);
      } else {
        await api.post('/absences', payload);
      }

      setShowModal(false);
      setEditingAbsence(null);
      resetForm();
      loadAbsences();
    } catch (error) {
      console.error('[v0] Error saving absence:', error);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await api.put(`/absences/${id}/approve`);
      loadAbsences();
    } catch (error) {
      console.error('[v0] Error approving absence:', error);
    }
  };

  const handleReject = async (id: string) => {
    try {
      await api.put(`/absences/${id}/reject`);
      loadAbsences();
    } catch (error) {
      console.error('[v0] Error rejecting absence:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Segur que vols eliminar aquesta absència?')) return;
    try {
      await api.delete(`/absences/${id}`);
      loadAbsences();
    } catch (error) {
      console.error('[v0] Error deleting absence:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      employeeId: '',
      type: 'VACANCES',
      startDate: '',
      endDate: '',
      reason: '',
    });
  };

  const getTypeConfig = (type: string) => {
    return ABSENCE_TYPES.find(t => t.value === type) || ABSENCE_TYPES[ABSENCE_TYPES.length - 1];
  };

  const calculateDays = (start: string, end: string): number => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/rrhh')}
            title="Tornar enrere"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Absències i Vacances</h1>
            <p className="text-slate-500">Gestió de vacances, baixes i permisos</p>
          </div>
        </div>
        <Button onClick={() => { resetForm(); setEditingAbsence(null); setShowModal(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Sol·licitud
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pendents d'Aprovar</p>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Aprovades</p>
                <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Dies Totals</p>
                <p className="text-2xl font-bold">{stats.totalDays}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Any</p>
                <p className="text-2xl font-bold">{filters.year}</p>
              </div>
              <Clock className="w-8 h-8 text-slate-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="rounded-xl">
        <CardHeader className="pb-3 border-b">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-3">
            <div>
              <Label>Any</Label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                value={filters.year}
                onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
              >
                {[2024, 2025, 2026, 2027].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Empleat</Label>
              <ExcelFilterDropdown
                label="Empleat"
                options={employees.map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                selected={filters.employeeId ? filters.employeeId.split(',') : []}
                onApply={(values) => setFilters(prev => ({ ...prev, employeeId: values.join(',') }))}
                className="w-full"
              />
            </div>
            <div>
              <Label>Tipus</Label>
              <ExcelFilterDropdown
                label="Tipus"
                options={ABSENCE_TYPES.map(type => ({ value: type.value, label: type.label }))}
                selected={filters.type ? filters.type.split(',') : []}
                onApply={(values) => setFilters(prev => ({ ...prev, type: values.join(',') }))}
                className="w-full"
              />
            </div>
            <div>
              <Label>Estat</Label>
              <ExcelFilterDropdown
                label="Estat"
                options={[
                  { value: 'PENDENT', label: 'Pendent' },
                  { value: 'APROVADA', label: 'Aprovada' },
                  { value: 'REBUTJADA', label: 'Rebutjada' },
                ]}
                selected={filters.status ? filters.status.split(',') : []}
                onApply={(values) => setFilters(prev => ({ ...prev, status: values.join(',') }))}
                className="w-full"
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => setFilters({
                employeeId: '',
                type: '',
                status: '',
                year: new Date().getFullYear().toString(),
              })}>
                Netejar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-xl">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empleat</TableHead>
                <TableHead>Tipus</TableHead>
                <TableHead>Data Inici</TableHead>
                <TableHead>Data Fi</TableHead>
                <TableHead className="text-center">Dies</TableHead>
                <TableHead>Estat</TableHead>
                <TableHead>Motiu</TableHead>
                <TableHead className="text-right">Accions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Carregant...
                  </TableCell>
                </TableRow>
              ) : absences.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No hi ha absències registrades
                  </TableCell>
                </TableRow>
              ) : (
                absences.map(absence => {
                  const typeConfig = getTypeConfig(absence.type);
                  const statusConfig = STATUS_CONFIG[absence.status] || STATUS_CONFIG.PENDENT;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <TableRow key={absence.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          {absence.employee?.firstName} {absence.employee?.lastName}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={typeConfig.color}>
                          {typeConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(absence.startDate)}</TableCell>
                      <TableCell>{formatDate(absence.endDate)}</TableCell>
                      <TableCell className="text-center font-medium">
                        {absence.totalDays}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {absence.reason || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {absence.status === 'PENDENT' && (
                            <>
                              <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleApprove(absence.id)}>
                                Aprovar
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleReject(absence.id)}>
                                Rebutjar
                              </Button>
                            </>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(absence.id)}>
                            Eliminar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingAbsence ? 'Editar Absència' : 'Nova Sol·licitud'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Empleat *</Label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                value={formData.employeeId}
                onChange={(e) => setFormData(prev => ({ ...prev, employeeId: e.target.value }))}
                required
              >
                <option value="">Selecciona empleat</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.code} - {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Tipus *</Label>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                required
              >
                {ABSENCE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Inici *</Label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label>Data Fi *</Label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  required
                />
              </div>
            </div>
            {formData.startDate && formData.endDate && (
              <div className="p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-800">
                  Total dies: <strong>{calculateDays(formData.startDate, formData.endDate)}</strong>
                </p>
              </div>
            )}
            <div>
              <Label>Motiu</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Motiu de l'absència..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel·lar
              </Button>
              <Button type="submit">
                {editingAbsence ? 'Guardar' : 'Sol·licitar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
